import { StyleSpecification } from "maplibre-gl";

import { LayerType, MapProvider } from "@/types/map";

import noBuildings from "./no_buildings.json";
import noLabelNoBuilding from "./no_label_no_building.json";

export type BaseMapStyle = {
  title: string;
  description: string;
  value: StyleSpecification | string;
  provider: "mapbox" | "carto";
  thumbnail?: string;
};

export const DEFAULT_GEOM_PRECISION = 6;
export const MAX_AREA = 50000;

export const SUFFIX_LAYER_BVARTA = [
  "circle",
  "count",
  "unclustered-points",
  "label",
  "rank",
  "fill",
  "outline",
  "outline-selected",
  "line-stroke",
  "centroid",
  "polygon"
];

export const SUBLAYERS = (datasetId: string) => {
  return {
    [LayerType.POINT]: {
      circle: `${datasetId}-circle`,
      count: `${datasetId}-count`,
      unclustered: `${datasetId}-unclustered-points`,
      label: `${datasetId}-label`,
      rank: `${datasetId}-rank`
    },
    [LayerType.POLYGON]: {
      fill: `${datasetId}-fill`,
      outline: `${datasetId}-outline`,
      outlineSelected: `${datasetId}-outline-selected`,
      label: `${datasetId}-label`,
      rank: `${datasetId}-rank`
    },
    [LayerType.ADMINISTRATIVE]: {
      fill: `${datasetId}-fill`,
      outline: `${datasetId}-outline`,
      outlineSelected: `${datasetId}-outline-selected`,
      label: `${datasetId}-label`,
      rank: `${datasetId}-rank`
    },
    [LayerType.GRID]: {
      fill: `${datasetId}-fill`,
      outline: `${datasetId}-outline`,
      outlineSelected: `${datasetId}-outline-selected`,
      label: `${datasetId}-label`,
      rank: `${datasetId}-rank`
    },
    [LayerType.PROFILING]: {
      fill: `${datasetId}-fill`,
      outline: `${datasetId}-outline`,
      outlineSelected: `${datasetId}-outline-selected`,
      label: `${datasetId}-label`,
      rank: `${datasetId}-rank`
    },
    [LayerType.H3]: {
      fill: `${datasetId}-fill`,
      outline: `${datasetId}-outline`,
      outlineSelected: `${datasetId}-outline-selected`,
      label: `${datasetId}-label`,
      rank: `${datasetId}-rank`
    },
    [LayerType.GEOHASH]: {
      fill: `${datasetId}-fill`,
      outline: `${datasetId}-outline`,
      outlineSelected: `${datasetId}-outline-selected`,
      label: `${datasetId}-label`,
      rank: `${datasetId}-rank`
    },
    [LayerType.LINE]: {
      stroke: `${datasetId}-line-stroke`,
      label: `${datasetId}-label`,
      rank: `${datasetId}-rank`
    }
  };
};

export const GLOBAL_ADMIN_SOURCE_ID = "global-admin-source-layer";
export const COUNTRY_SOURCE_ID = "country-source-layer";
export const ROUTING_SOURCE_ID = "isochrone-routing-layer";
export const CIRCE_RADIUS_SOURCE_ID = "circe-radius-layer";
export const MAPBOX_GL_DRAW_SOURCE_ID = "gl-draw";

export const CATCHMENT_SOURCE_IDS = [
  CIRCE_RADIUS_SOURCE_ID,
  COUNTRY_SOURCE_ID,
  ROUTING_SOURCE_ID
];

export const MARKER_FOCUS_ZOOM_LEVEL = 12;
export const MARKER_FOCUS_ZOOM_FOCUS = 16;

export const GeometryType = {
  POINT: "Point"
};

// CSS class names used in map UI
export const MARKER_LABEL_CLASS = "marker-label";
export const MARKER_RANK_CLASS = "marker-rank";

export const COORDINATE_LABLE = {
  LATITUDE: "Latitude",
  LONGITUDE: "Longitude"
};

export const DRAW_POPUP_INFO_SOURCE = "draw-popup-info-source";
export const DRAW_POPUP_INFO_LAYER = "draw-popup-info-layer";

// H3 Resolution configurations
export const H3_RESOLUTION_CONFIGS = [
  { label: "609,788.44 Km2", radius: "483.05 Km", value: "1" },
  { label: "86,801.78 Km2", radius: "182.51Km", value: "2" },
  { label: "12,393.43Km2", radius: "68.97Km", value: "3" },
  { label: "1,770.34Km2", radius: "26.07Km", value: "4" },
  { label: "252.90Km2", radius: "9.85Km", value: "5" },
  { label: "36.12Km2", radius: "3.72Km", value: "6" },
  { label: "5.16Km2", radius: "1.40Km", value: "7" },
  { label: "0.73Km2", radius: "0.53Km", value: "8" },
  { label: "0.10Km2", radius: "0.20Km", value: "9" },
  { label: "0.01Km2", radius: "0.07Km", value: "10" }
];

export const MAP_STYLES = {
  street: {
    title: "Street",
    provider: MapProvider.CARTO,
    description: "Standard map with labels and buildings",
    value: "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json",
    thumbnail: "images/carto_street.png"
  },
  no_labels: {
    title: "No Labels",
    provider: MapProvider.CARTO,
    description: "Street map without labels, showing only features",
    value:
      "https://basemaps.cartocdn.com/gl/voyager-nolabels-gl-style/style.json",
    thumbnail: "images/carto_no_label.png"
  },
  lite: {
    title: "Lite",
    provider: MapProvider.CARTO,
    description: "Minimal map without labels and buildings.",
    value: noLabelNoBuilding as StyleSpecification,
    thumbnail: "images/carto_no_label_building.png"
  },
  no_buildings: {
    title: "No Buildings",
    provider: MapProvider.CARTO,
    description: "Street map without buildings, keeping labels visible",
    value: noBuildings as StyleSpecification,
    thumbnail: "images/carto_no_building.png"
  },
  mapbox_street: {
    title: "Transit",
    provider: MapProvider.MAPBOX,
    description: "Map with roads, transit, and key facilities.",
    value: "mapbox://styles/mapbox/streets-v12",
    thumbnail: "images/mapbox_transit.png"
  },
  countour: {
    title: "Contour",
    provider: MapProvider.MAPBOX,
    description: "Terrain map with contour lines, labels, and buildings.",
    value: "mapbox://styles/mapbox/outdoors-v12",
    thumbnail: "images/mapbox_contour.png"
  },
  satellite: {
    title: "Satellite",
    provider: MapProvider.MAPBOX,
    description: "High-resolution imagery map with labels and buildings.",
    value: "mapbox://styles/mapbox/satellite-streets-v12",
    thumbnail: "images/mapbox_satellite.png"
  },
  dark: {
    title: "Dark",
    provider: MapProvider.MAPBOX,
    description: "Dark map with labels and buildings",
    value: "mapbox://styles/mapbox/dark-v11",
    thumbnail: "images/mapbox_dark.png"
  }
} satisfies Record<string, BaseMapStyle>;

export const MAP_STYLE_PRESETS = { mapbox: "mapbox", style_url: "style-url" };
